import { EventCardElementMixin } from "~/dynamic-components/standard-components/event-card/event-card-element-mixin";

// One module-level interval shared by every countdown element on the page,
// alive only while at least one element is mounted.

const TICK_INTERVAL_MS = 1000;

// The two element trees name the same column differently: DCE exposes
// `startTime`/`earlyJoinMinutes`, FanWeb exposes `startTimestamp` — read both.
function entityNumber(entity, ...fieldNames) {
  for (const fieldName of fieldNames) {
    const value = Number(entity?.[fieldName] ?? 0);
    if (Number.isFinite(value) && value > 0) {
      return value;
    }
  }

  return 0;
}

const subscribers = new Set();
let intervalId = null;
let sharedNow = Math.floor(Date.now() / 1000);

function tick() {
  sharedNow = Math.floor(Date.now() / 1000);
  subscribers.forEach(notify => notify(sharedNow));
}

function subscribe(notify) {
  subscribers.add(notify);
  if (intervalId === null && typeof window !== "undefined") {
    intervalId = window.setInterval(tick, TICK_INTERVAL_MS);
  }
}

function unsubscribe(notify) {
  subscribers.delete(notify);
  if (subscribers.size === 0 && intervalId !== null && typeof window !== "undefined") {
    window.clearInterval(intervalId);
    intervalId = null;
  }
}

export const EventCountdownElementMixin = {
  extends: EventCardElementMixin,
  data() {
    return {
      countdownNow: sharedNow,
    };
  },
  created() {
    this.$_countdownNotify = now => {
      this.countdownNow = now;
    };
    subscribe(this.$_countdownNotify);
  },
  unmounted() {
    unsubscribe(this.$_countdownNotify);
  },
  computed: {
    secondsUntilStart() {
      const start = entityNumber(this.entity, "startTime", "startTimestamp");
      if (start <= 0 || this.countdownNow >= start) {
        return 0;
      }

      return start - this.countdownNow;
    },
    secondsUntilEarlyAccess() {
      if (!this.entity?.allowEarlyJoin) {
        return 0;
      }

      const minutes = entityNumber(this.entity, "earlyJoinMinutes");
      if (minutes <= 0) {
        return 0;
      }

      const start = entityNumber(this.entity, "startTime", "startTimestamp");
      const earlyWindowSeconds = minutes * 60;
      const opensAt = start - earlyWindowSeconds;
      if (start <= 0 || opensAt <= 0 || this.countdownNow >= opensAt) {
        return 0;
      }

      return opensAt - this.countdownNow;
    },
    hasEarlyAccessWindow() {
      return Boolean(this.entity?.allowEarlyJoin) && entityNumber(this.entity, "earlyJoinMinutes") > 0;
    },
  },
};
